import { THEME } from "./chart_helpers"

function formatM(value) {
  return "$" + Number(value).toLocaleString("en-NZ", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
}

export function debtPayoffDatasets(chartData) {
  return [
    {
      label: chartData.datasets[0].label,
      data: chartData.datasets[0].data,
      borderColor: THEME.violet,
      backgroundColor: THEME.violetLight,
      fill: true,
      tension: 0.3,
      pointRadius: 3,
      pointHoverRadius: 5
    },
    {
      label: chartData.datasets[1].label,
      data: chartData.datasets[1].data,
      borderColor: THEME.emerald,
      backgroundColor: THEME.emeraldLight,
      fill: true,
      tension: 0.3,
      pointRadius: 3,
      pointHoverRadius: 5
    }
  ]
}

export function renderStrategyComparison(container, data) {
  const comparison = data.comparison
  const better = comparison.better_strategy
  const neverPaid = comparison.minimum_never_paid_off
  const failed = comparison.strategies_failed

  if (failed) {
    const extraMsg = comparison.minimum_extra_needed
      ? `<p class="text-sm text-amber-700 mt-3">Adding just <span class="font-semibold">${formatM(comparison.minimum_extra_needed)}/month</span> in extra payments would be enough to pay everything off. Try entering that amount above.</p>`
      : ""

    container.innerHTML = `
      <div class="rounded-lg border-2 border-amber-300 bg-amber-50 p-5">
        <p class="font-semibold text-amber-800 text-lg">Extra payments needed</p>
        <p class="text-sm text-amber-700 mt-1">One or more debts have minimum payments below their monthly interest. These balances grow while other debts are being paid off, and by the time the other payments free up, the compounded balance is too large to recover from.</p>
        <p class="text-sm text-amber-700 mt-2">See the warnings below for which debts are affected.</p>
        ${extraMsg}
      </div>
    `
    return
  }

  const snowballBorder = better === "snowball"
    ? "border-emerald-400 ring-1 ring-emerald-200" : "border-stone-200"
  const avalancheBorder = better === "avalanche"
    ? "border-emerald-400 ring-1 ring-emerald-200" : "border-stone-200"

  const snowballLabel = better === "snowball"
    ? `<span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-50 text-emerald-700 ml-2">Best</span>` : ""
  const avalancheLabel = better === "avalanche"
    ? `<span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-50 text-emerald-700 ml-2">Best</span>` : ""

  const snowballSavings = neverPaid
    ? `<span class="font-medium text-stone-500">N/A</span>`
    : `<span class="font-medium text-emerald-600">${formatM(comparison.snowball_savings)}</span>`
  const avalancheSavings = neverPaid
    ? `<span class="font-medium text-stone-500">N/A</span>`
    : `<span class="font-medium text-emerald-600">${formatM(comparison.avalanche_savings)}</span>`

  container.innerHTML = `
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div class="rounded-lg border-2 ${snowballBorder} p-4">
        <div class="flex items-center mb-3">
          <h4 class="font-semibold text-stone-800">Snowball</h4>
          ${snowballLabel}
        </div>
        <p class="text-sm text-stone-500 mb-3">Smallest balance first</p>
        <div class="space-y-2">
          <div class="flex justify-between text-sm">
            <span class="text-stone-600">Time to debt-free</span>
            <span class="font-medium text-stone-800">${comparison.snowball_months} months</span>
          </div>
          <div class="flex justify-between text-sm">
            <span class="text-stone-600">Total interest</span>
            <span class="font-medium text-stone-800">${formatM(comparison.snowball_interest)}</span>
          </div>
          <div class="flex justify-between text-sm">
            <span class="text-stone-600">Saved vs minimums</span>
            ${snowballSavings}
          </div>
        </div>
      </div>
      <div class="rounded-lg border-2 ${avalancheBorder} p-4">
        <div class="flex items-center mb-3">
          <h4 class="font-semibold text-stone-800">Avalanche</h4>
          ${avalancheLabel}
        </div>
        <p class="text-sm text-stone-500 mb-3">Highest rate first</p>
        <div class="space-y-2">
          <div class="flex justify-between text-sm">
            <span class="text-stone-600">Time to debt-free</span>
            <span class="font-medium text-stone-800">${comparison.avalanche_months} months</span>
          </div>
          <div class="flex justify-between text-sm">
            <span class="text-stone-600">Total interest</span>
            <span class="font-medium text-stone-800">${formatM(comparison.avalanche_interest)}</span>
          </div>
          <div class="flex justify-between text-sm">
            <span class="text-stone-600">Saved vs minimums</span>
            ${avalancheSavings}
          </div>
        </div>
      </div>
    </div>
  `
}

export function renderPayoffSchedule(tbody, schedule) {
  tbody.innerHTML = schedule.map(row => {
    return `
      <tr class="border-b border-stone-100 hover:bg-stone-50">
        <td class="px-4 py-2 text-stone-700">${row.month}</td>
        <td class="px-4 py-2 text-right money-value">${formatM(row.total_balance)}</td>
        <td class="px-4 py-2 text-right text-stone-600">${formatM(row.payment)}</td>
        <td class="px-4 py-2 text-right text-stone-500">${formatM(row.interest)}</td>
      </tr>
    `
  }).join("")
}

export function renderDebtPayoffInsights(container, insights) {
  if (!insights || insights.length === 0) {
    container.innerHTML = `
      <div class="rounded-lg bg-stone-50 p-4 text-sm text-stone-600">
        Enter your debts and extra payment amount to see personalised insights.
      </div>
    `
    return
  }

  const TYPE_STYLES = {
    success: { bg: "bg-emerald-50 border-emerald-200", text: "text-emerald-800", icon: "&#10003;" },
    warning: { bg: "bg-amber-50 border-amber-200", text: "text-amber-800", icon: "&#9888;" },
    info: { bg: "bg-blue-50 border-blue-200", text: "text-blue-800", icon: "&#8505;" },
    milestone: { bg: "bg-violet-50 border-violet-200", text: "text-violet-800", icon: "&#9733;" }
  }

  container.innerHTML = insights.map(insight => {
    const style = TYPE_STYLES[insight.type] || TYPE_STYLES.info

    return `
      <div class="rounded-lg border ${style.bg} p-3 text-sm ${style.text} flex gap-2">
        <span class="mt-0.5 shrink-0">${style.icon}</span>
        <span>${insight.message}</span>
      </div>
    `
  }).join("")
}
